import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Button from "../../../components/common/button/Button";
import ImageSelect from "../../../components/common/image-select/ImageSelect";
import styles from "./CreateProduct.module.scss";
import ProductsService from "../../../api/services/products.service";
import { CreateProductVariantImagesType } from "../../../api/types/products.types";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const CreateVariantImages = () => {
  const { t } = useTranslation("translation");
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [formValues, setFormValues] = useState<
    Partial<CreateProductVariantImagesType>
  >({
    variant_ids: "",
    images: [],
  });

  const { data: variants, loaderRef: variantsRef } = useInfiniteScroll({
    fetchFn: async (page) => {
      const res = await ProductsService.getAllVariants(Number(id), page, 20);
      return res.data.data;
    },
    pageSize: 20,
  });

  const handleImagesChange = (images: any[]) => {
    setFormValues((prev) => ({ ...prev, images }));
  };

  const handleVariantToggle = (variantId: number) => {
    const selectedIds = formValues.variant_ids
      ? formValues.variant_ids.split(",").filter(Boolean).map(Number)
      : [];

    const exists = selectedIds.includes(variantId);
    const updated = exists
      ? selectedIds.filter((id) => id !== variantId)
      : [...selectedIds, variantId];

    setFormValues((prev) => ({
      ...prev,
      variant_ids: updated.join(","),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValues.images || formValues.images.length === 0) {
      toast.error(t("products_details.select_images"));
      return;
    }
    if (!formValues.variant_ids) {
      toast.error(t("products_details.select_variants"));
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("variant_ids", formValues.variant_ids);

      formValues.images.forEach((img) => {
        if (img.file) {
          formData.append("images", img.file);
        }
      });

      await ProductsService.createProductVariantImages(formData, Number(id));
      toast.success(t("products_details.images_uploaded"));
      setSubmitted(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewImages = () => {
    setFormValues({
      variant_ids: "",
      images: [],
    });
    setSubmitted(false);
    setResetKey((prev) => prev + 1);
  };

  const getLocalizedVariantName = (
    variant: any,
    lang: string,
    t: any,
  ): string => {
    const priorities: Record<string, (keyof typeof variant)[]> = {
      az: ["name_az", "name_en", "name_de", "name_es"],
      en: ["name_en", "name_az", "name_de", "name_es"],
      es: ["name_es", "name_en", "name_az", "name_de"],
      de: ["name_de", "name_en", "name_az", "name_es"],
    };

    const order = priorities[lang] || priorities.en;

    for (const key of order) {
      const value = variant[key];
      if (value && typeof value === "string" && value.trim() !== "") {
        return value;
      }
    }

    return t("products_details.unnamed_variant");
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <TitleUpdater title={t("add_images")} />
      <div className={styles.grid}>
        <div className={styles.left}>
          <div className={styles.form_category}>
            <p className={styles.title}>{t("columns.images")}</p>
            <div className={styles.form_content_column}>
              <ImageSelect
                onImagesChange={handleImagesChange}
                resetKey={resetKey}
              />
            </div>
          </div>
        </div>

        <div className={styles.left}>
          <div className={styles.form_category}>
            <p className={styles.title}>
              {t("products_details.assign_variants")}
            </p>
            <div className={styles.form_content_column}>
              {variants.length > 0 ? (
                variants.map((variant: any) => {
                  const isChecked = formValues.variant_ids
                    ?.split(",")
                    .includes(String(variant.id));

                  const variantName = getLocalizedVariantName(
                    variant,
                    i18n.language,
                    t,
                  );

                  return (
                    <label
                      key={variant.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleVariantToggle(variant.id)}
                      />
                      {variantName}
                      {variantsRef && (
                        <div
                          ref={variantsRef}
                          className={styles.loaderTrigger}
                        />
                      )}
                    </label>
                  );
                })
              ) : (
                <p>{t("products_details.no_variants")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.other_btns}>
        {!submitted ? (
          <Button type="submit" disabled={loading}>
            {loading ? t("buttons.uploading") : t("submit")}
          </Button>
        ) : (
          <div className={styles.other_btns}>
            <Button onClick={handleAddNewImages}>
              {t("products_details.add_new_images")}
            </Button>
            <Button
              onClick={() => navigate("/dashboard/products")}
              className={styles.add_variant}
            >
              {t("products_details.go_to_products")}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

export default CreateVariantImages;
